import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { FAQ } from '../types';

interface FAQContextType {
  faqs: FAQ[];
  loading: boolean;
  error: string | null;
  addFAQ: (faq: Omit<FAQ, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateFAQ: (id: number, faq: Partial<FAQ>) => Promise<void>;
  deleteFAQ: (id: number) => Promise<void>;
  getFAQ: (id: number) => FAQ | undefined;
  refreshFAQs: () => Promise<void>;
}

const FAQContext = createContext<FAQContextType | undefined>(undefined);

export const FAQProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [faqs, setFAQs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Supabase에서 FAQ 데이터 가져오기
  const fetchFAQs = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('faqs')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setFAQs(data || []);
    } catch (err) {
      console.error('Error fetching FAQs:', err);
      setError(err instanceof Error ? err.message : 'FAQ를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 초기 데이터 로드
  useEffect(() => {
    fetchFAQs();
  }, []);

  const addFAQ = async (faqData: Omit<FAQ, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      setError(null);

      const { error: insertError } = await supabase
        .from('faqs')
        .insert({
          question: faqData.question,
          answer: faqData.answer,
          sort_order: faqData.sort_order || 0,
          is_published: faqData.is_published ?? true,
          author: faqData.author || '관리자',
        });

      if (insertError) throw insertError;

      // 데이터 다시 불러오기
      await fetchFAQs();
    } catch (err) {
      console.error('Error adding FAQ:', err);
      setError(err instanceof Error ? err.message : 'FAQ 추가에 실패했습니다.');
      throw err;
    }
  };

  const updateFAQ = async (id: number, faqData: Partial<FAQ>) => {
    try {
      setError(null);

      const updateData: any = {};
      if (faqData.question !== undefined) updateData.question = faqData.question;
      if (faqData.answer !== undefined) updateData.answer = faqData.answer;
      if (faqData.sort_order !== undefined) updateData.sort_order = faqData.sort_order;
      if (faqData.is_published !== undefined) updateData.is_published = faqData.is_published;
      if (faqData.author !== undefined) updateData.author = faqData.author;

      const { error: updateError } = await supabase
        .from('faqs')
        .update(updateData)
        .eq('id', id);

      if (updateError) throw updateError;

      // 데이터 다시 불러오기
      await fetchFAQs();
    } catch (err) {
      console.error('Error updating FAQ:', err);
      setError(err instanceof Error ? err.message : 'FAQ 수정에 실패했습니다.');
      throw err;
    }
  };

  const deleteFAQ = async (id: number) => {
    try {
      setError(null);

      const { error: deleteError } = await supabase
        .from('faqs')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      // 로컬 상태에서 제거
      setFAQs(prev => prev.filter(faq => faq.id !== id));
    } catch (err) {
      console.error('Error deleting FAQ:', err);
      setError(err instanceof Error ? err.message : 'FAQ 삭제에 실패했습니다.');
      throw err;
    }
  };

  const getFAQ = (id: number) => {
    return faqs.find(faq => faq.id === id);
  };

  const refreshFAQs = async () => {
    await fetchFAQs();
  };

  return (
    <FAQContext.Provider value={{
      faqs,
      loading,
      error,
      addFAQ,
      updateFAQ,
      deleteFAQ,
      getFAQ,
      refreshFAQs
    }}>
      {children}
    </FAQContext.Provider>
  );
};

export const useFAQs = () => {
  const context = useContext(FAQContext);
  if (!context) {
    throw new Error('useFAQs must be used within a FAQProvider');
  }
  return context;
};
